import React, { useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { corUpdate, corDelete, corUploadChunked, corDeleteAttachment, corAttachmentUrl, corPdfUrl } from '../../../api';
import { toast } from '../../../hooks/use-toast';
import { Upload, Download, Eye, Trash2, Printer, Pencil, FileText, Image as ImageIcon, AlertTriangle, Clock } from 'lucide-react';
import { L, DIRECTION, STATUS, ENTITY_TYPE, CATEGORY, label, Pill, fmtSize } from './shared';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';

export const CorrespondenceDetail = ({ item, onClose, lang, onChanged, onEdit }) => {
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);
  if (!item) return null;

  const refresh = (d) => onChanged(d);

  const setStatus = async (status) => {
    try { refresh(await corUpdate(item.id, { status })); toast({ title: L(lang, 'Status updated', 'تم تحديث الحالة') }); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const upload = async (files) => {
    for (const f of Array.from(files || [])) {
      if (f.size > 25 * 1024 * 1024) { toast({ title: L(lang, `${f.name}: exceeds 25 MB`, `${f.name}: أكبر من 25 ميجا`), variant: 'destructive' }); continue; }
      setProgress({ name: f.name, pct: 0 });
      try {
        await corUploadChunked(item.id, f, (pct) => setProgress({ name: f.name, pct }));
        toast({ title: L(lang, `Uploaded ${f.name}`, `تم رفع ${f.name}`) });
      } catch (e) {
        toast({ title: e?.response?.data?.detail || L(lang, 'Upload failed', 'فشل الرفع'), variant: 'destructive' });
      }
    }
    setProgress(null);
    onChanged();
  };

  const removeAtt = async (a) => {
    if (!window.confirm(L(lang, `Delete ${a.filename}?`, `حذف ${a.filename}؟`))) return;
    await corDeleteAttachment(item.id, a.id); onChanged();
  };

  const removeItem = async () => {
    if (!window.confirm(L(lang, `Delete ${item.id} permanently?`, `حذف ${item.id} نهائياً؟`))) return;
    await corDelete(item.id); toast({ title: L(lang, 'Deleted', 'تم الحذف') }); onClose(); onChanged();
  };

  const rows = [
    [L(lang, 'Date', 'التاريخ'), item.date], [L(lang, 'Entity', 'الجهة'), `${item.entity} · ${label(ENTITY_TYPE, item.entity_type, lang)}`],
    [L(lang, 'Category', 'التصنيف'), label(CATEGORY, item.category, lang)], [L(lang, 'Reply due', 'استحقاق الرد'), item.due_at || '—'],
    [L(lang, 'CRM lead', 'عميل CRM'), item.lead_name ? `${item.lead_name} (${item.lead_id})` : '—'], [L(lang, 'Created by', 'أنشأها'), item.created_by || '—'],
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="cor-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 font-display text-xl">
            <span data-testid="cor-detail-ref" className="font-mono text-[#2563eb]">{item.id}</span>
            <Pill map={DIRECTION} value={item.direction} lang={lang} />
            <Pill map={STATUS} value={item.status} lang={lang} testId="cor-detail-status" />
            {item.overdue && <span className="inline-flex items-center gap-1 text-[11px] text-rose-600 font-semibold" data-testid="cor-overdue-flag"><AlertTriangle className="w-3.5 h-3.5" /> {L(lang, 'Overdue', 'متأخرة')}</span>}
          </DialogTitle>
        </DialogHeader>
        <h3 className="text-lg text-[#0a0a0a] mt-1" data-testid="cor-detail-subject">{item.subject}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
          {rows.map(([k, v]) => <div key={k} className="rounded-md bg-[#f8fafc] border border-[#e5e7eb] px-3 py-2"><div className="text-[10px] uppercase tracking-widest text-[#111827]/45">{k}</div><div className="text-sm text-[#111827] truncate">{v}</div></div>)}
        </div>
        {item.notes && <p className="text-sm text-[#111827]/70 whitespace-pre-wrap mt-3 rounded-md border border-[#e5e7eb] p-3">{item.notes}</p>}

        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold me-1">{L(lang, 'Status', 'الحالة')}:</span>
          {Object.keys(STATUS).map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`cor-set-status-${s}`} className={`h-8 px-3 rounded-full border text-xs transition-colors ${item.status === s ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white border-[#e5e7eb] text-[#111827]/70 hover:border-[#2563eb]/40'}`}>{label(STATUS, s, lang)}</button>
          ))}
          <div className="ms-auto flex gap-2">
            <Button onClick={() => onEdit(item)} className="ghost-button bg-transparent h-8 px-3 text-xs rounded-md" data-testid="cor-edit-btn"><Pencil className="w-3.5 h-3.5 me-1" /> {L(lang, 'Edit', 'تعديل')}</Button>
            <Button onClick={() => window.open(corPdfUrl(item.id), '_blank')} className="h-8 px-3 text-xs bg-[#0a0a0a] text-white rounded-md" data-testid="cor-print-btn"><Printer className="w-3.5 h-3.5 me-1" /> PDF</Button>
            <Button onClick={removeItem} className="h-8 px-3 text-xs bg-rose-600 hover:bg-rose-700 text-white rounded-md" data-testid="cor-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        <div className="mt-5" data-testid="cor-attachments">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold">{L(lang, 'Documents', 'المستندات')} ({item.attachments?.length || 0})</h4>
            <span className="text-[10px] text-[#111827]/45">PDF · JPG · PNG · WEBP · DOCX · {L(lang, 'max 25 MB', 'حد أقصى 25 ميجا')}</span>
          </div>
          <label className={`flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#2563eb]/40 bg-[#f8fafc] py-5 cursor-pointer hover:border-[#2563eb] transition-colors ${progress ? 'opacity-70 pointer-events-none' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}>
            <Upload className="w-4 h-4 text-[#2563eb]" />
            <span className="text-sm text-[#2563eb] font-semibold">{progress ? `${progress.name} — ${progress.pct}%` : L(lang, 'Upload documents (drag & drop or click)', 'رفع مستندات (اسحب وأفلت أو اضغط)')}</span>
            <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid="cor-file-input" />
          </label>
          {progress && <div className="h-1.5 rounded-full bg-[#e5e7eb] mt-2 overflow-hidden"><div className="h-full bg-[#2563eb] transition-all" style={{ width: `${progress.pct}%` }} data-testid="cor-upload-progress" /></div>}
          <ul className="mt-3 space-y-2">
            {(item.attachments || []).map((a) => (
              <li key={a.id} className="flex items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white px-3 py-2" data-testid={`cor-att-${a.id}`}>
                {a.content_type?.startsWith('image/') ? <img src={corAttachmentUrl(item.id, a.id, true)} alt="" className="w-10 h-10 rounded object-cover bg-[#f8fafc]" /> : <div className="w-10 h-10 rounded bg-[#2563eb]/10 flex items-center justify-center">{a.content_type === 'application/pdf' ? <FileText className="w-5 h-5 text-[#2563eb]" /> : <ImageIcon className="w-5 h-5 text-[#2563eb]" />}</div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#111827] truncate">{a.filename}</div>
                  <div className="text-[10px] text-[#111827]/50 flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(a.uploaded_at).toLocaleDateString()} · {fmtSize(a.size)} · {a.uploaded_by}</div>
                </div>
                <a href={corAttachmentUrl(item.id, a.id, true)} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-[#f8fafc] text-[#2563eb]" title="View" data-testid={`cor-att-view-${a.id}`}><Eye className="w-4 h-4" /></a>
                <a href={corAttachmentUrl(item.id, a.id)} className="p-1.5 rounded hover:bg-[#f8fafc] text-[#111827]/70" title="Download" data-testid={`cor-att-download-${a.id}`}><Download className="w-4 h-4" /></a>
                <button onClick={() => removeAtt(a)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" data-testid={`cor-att-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </div>

        {item.history?.length > 0 && (
          <div className="mt-5">
            <h4 className="text-[10px] uppercase tracking-widest text-[#2563eb] font-bold mb-2">{L(lang, 'History', 'السجل')}</h4>
            <ul className="space-y-1 text-xs text-[#111827]/65">
              {[...item.history].reverse().map((h, i) => <li key={i} className="flex gap-2"><span className="text-[#111827]/40 shrink-0">{new Date(h.at).toLocaleString()}</span><span>{h.action}</span><span className="text-[#111827]/40 ms-auto">{h.by}</span></li>)}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
